// /settings/pair/dissolve — pair dissolution confirmation page.
//
// Friction UX (per W2 §4.4): require typing the exact pair name to enable
// the destructive action.  Both pair members can dissolve; either one
// soft-deletes the Pair + emits pair.dissolved.
//
// W40.5. Same omission as its sibling `/settings/pair`: the body was a bare
// `<div className="flex flex-col gap-4">` with no `PageContainer`, so the banner
// and the form ran edge-to-edge — measured at 0px on a 393px viewport against
// 16-18px elsewhere. The banner moved inside the container with the form rather
// than staying full-bleed, so the two read as one card instead of a full-width
// strip above an inset one. `as="div"` because `MobileShell` already owns the
// page's `<main>`.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import { PageHero } from "@/components/primitives/PageHero";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FormErrorBanner, FormField } from "@/components/auth/FormField";
import { PageContainer } from "@/components/layout";
import { ApiError, dissolvePair, readPair } from "@/lib/api/pair";
import { pairErrorMessage } from "@/lib/i18n/pair";
import { dissolveConfirmSchema } from "@/lib/validation/pair";
import { InfoBanner } from "@/components/primitives/InfoBanner";
import { useToast } from "@/components/ui/toast";
import { queryKeys } from "@/lib/queries/keys";
import { useAuth } from "@/lib/auth/AuthProvider";

export function DissolvePairPage() {
  const nav = useNavigate();
  const { refresh, pairId: authPairId, status: authStatus } = useAuth();
  const qc = useQueryClient();
  const { push: pushToast } = useToast();

  const pairQuery = useQuery({
    queryKey: queryKeys.pairs.detail(authPairId ?? "active"),
    queryFn: () => readPair(authPairId!),
    enabled: authStatus === "authenticated" && Boolean(authPairId),
    retry: false,
  });

  const [confirmName, setConfirmName] = useState("");
  const [nameError, setNameError] = useState<string | null>(null);

  const dissolve = useMutation({
    mutationFn: () => {
      const pairId = pairQuery.data?.pair.id;
      if (!pairId) throw new ApiError(404, "PAIR_NOT_FOUND", "找不到 Pair");
      return dissolvePair(pairId);
    },
    onSuccess: async () => {
      await refresh();
      void qc.invalidateQueries({ queryKey: queryKeys.pairs.all });
      pushToast({
        variant: "default",
        message: "Pair 已解除, 7 天內可恢復。",
      });
      nav("/pair");
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        pushToast({
          variant: "danger",
          message: pairErrorMessage(err.code, err.message),
        });
      }
    },
  });

  const pairName = pairQuery.data?.pair.name ?? "";
  const matches = confirmName.trim() === pairName;

  useEffect(() => {
    setNameError(null);
  }, [confirmName]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setNameError(null);
    if (!matches) {
      setNameError("請輸入完整情名稱以確認");
      return;
    }
    const parsed = dissolveConfirmSchema.safeParse({
      confirm_pair_name: confirmName,
    });
    if (!parsed.success) {
      setNameError(parsed.error.issues[0]?.message ?? "請輸入完整情名稱以確認");
      return;
    }
    dissolve.mutate();
  };

  return (
    <div data-testid="dissolve-pair-page" className="flex flex-col">
      <PageHero
          // W52.0 D-9 — ADR-0115 named this page as the one with 「none at
          // all」. On the screen where someone is about to end a shared
          // history, the only exit was the browser back button.
          crumbs={[
            { to: "/settings/pair", label: "空間設定", testId: "dissolve-back" },
            { label: "解除配對" },
          ]}
          eyebrow="解除"
          title="解除配對"
          subtitle="解除後你們會各自回到未配對狀態, 之前的日記和紀念日會保留在對方的空間裡。"
        />
      <PageContainer as="div">
        <InfoBanner tone="partner" data-testid="dissolve-grace-info">
          7 天保留期: 解除後 7 天內任一方都可以重新建立 Pair, 之前的內容會在重新配對後可見。
        </InfoBanner>
        <Card>
          <form onSubmit={submit} noValidate className="p-6 flex flex-col gap-4">
            <p className="text-body text-ink-2">
              這是不可逆的動作, 請確認。為了避免誤刪, 請輸入 Pair 名稱 <strong>{pairName}</strong>{" "}
              以繼續。
            </p>
            <FormErrorBanner message={nameError} />
            <FormField
              name="confirm_pair_name"
              label={`輸入 「${pairName}」 以確認`}
              value={confirmName}
              onChange={setConfirmName}
              invalid={Boolean(nameError)}
              errorMessage={nameError ?? undefined}
              disabled={dissolve.isPending || pairQuery.isLoading}
              data-testid="confirm-pair-name"
            />
            <div className="flex gap-3 justify-end pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => nav("/settings/pair")}
                disabled={dissolve.isPending}
                data-testid="cancel-dissolve"
              >
                返回
              </Button>
              <Button
                type="submit"
                variant="destructive"
                disabled={!matches || dissolve.isPending}
                data-testid="confirm-dissolve"
                leadingIcon={
                  dissolve.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null
                }
              >
                {dissolve.isPending ? "解除中…" : "確認解除"}
              </Button>
            </div>
          </form>
        </Card>
      </PageContainer>
    </div>
  );
}
